// src/components/NewsGridThree.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import PostMeta from "./PostMeta";
import { imgOrPlaceholder } from "../utils/format";
import { useTranslation } from "../i18n";
import { withLangPrefix } from "../lib/i18nRoutes";
import api from "../lib/api";

/**
 * NewsGridThree - Professional Editorial Component
 * Features:
 * - Translation-aware fetching and normalization
 * - Responsive grid-to-list toggle
 * - Glassmorphism & Modern Typography
 * - RTL Support
 */

/* --------- Constants & Fallbacks --------- */
const STORAGE_LANG_KEY = process.env.REACT_APP_LANG_KEY || "site_lang";
const DEFAULT_LANG = "en";
const SAFE_LOCALES = ["en", "fr", "ar", "de"];
const NEWS_POLL_MS = 60_000;
const FALLBACK = (w, h) => `https://picsum.photos/${w}/${h}`;

/* --------- Translation Helpers --------- */
function cleanTranslatedString(v) {
  if (typeof v !== "string") return v;
  return v.replace(/\s*Translated\s*\([^)]+\)\s*$/i, "").trim();
}

function getTranslatedField(item, field, lang) {
  if (!item) return null;
  const L = (lang || DEFAULT_LANG).toLowerCase().split(/[-_]/)[0];

  if (Array.isArray(item.translations)) {
    const found = item.translations.find(tr =>
      tr?.target_lang?.toLowerCase().startsWith(L)
    );
    if (found && found[field]) return cleanTranslatedString(found[field]);
  }

  if (item.translated?.[L]?.[field]) return cleanTranslatedString(item.translated[L][field]);

  const suffix = `${field}_${L}`;
  if (item[suffix]) return cleanTranslatedString(item[suffix]);

  return cleanTranslatedString(item[field] ?? null);
}

function normalizeArticleForLang(article, lang) {
  if (!article || typeof article !== "object") return article;
  const a = { ...article };

  const tTitle = getTranslatedField(a, "title", lang);
  const tExcerpt = getTranslatedField(a, "excerpt", lang);
  const tCategory = getTranslatedField(a, "category", lang);
  const tImage = getTranslatedField(a, "image", lang) ?? getTranslatedField(a, "thumbnail", lang);

  a._translatedTitle = tTitle ?? null;
  a._translatedExcerpt = tExcerpt ?? null;
  a._translatedCategory = tCategory ?? null;
  
  const slug = a.slug ?? a.id ?? "";
  const hrefPath = typeof a.url === "string" && a.url.startsWith("/articles/") 
    ? a.url 
    : `/articles/${encodeURIComponent(slug)}`;
    
  a.href = withLangPrefix(hrefPath, lang);
  a.image_url = tImage ?? a.image ?? a.thumbnail ?? null;

  return a;
}

/* --------- Sub-Component: Grid Card --------- */
function GridCard({ a, lang }) {
  const title = a._translatedTitle || getTranslatedField(a, "title", lang) || "—";
  const image = a.image_url || FALLBACK(900, 540);
  const category = a._translatedCategory || getTranslatedField(a, "category", lang);

  return (
    <div className="col-12 col-md-6 col-lg-4">
      <Link to={a.href} className="pro-grid-card shadow-sm">
        <div className="card-media">
          <img src={imgOrPlaceholder(image, 600, 450)} alt={title} loading="lazy" />
          {category && <span className="grid-category-badge">{category}</span>}
        </div>
        <div className="card-overlay">
          <div className="overlay-content">
            <div className="mb-2">
              <PostMeta date={a.published_at || a.created_at} locale={lang} className="text-white-50 small" />
            </div>
            <h5 className="grid-title line-clamp-2">{title}</h5>
          </div>
        </div>
      </Link>
    </div>
  );
}

/* --------- Sub-Component: List Row --------- */
function ListRow({ a, lang }) {
  const title = a._translatedTitle || getTranslatedField(a, "title", lang) || "Untitled";
  const excerpt = a._translatedExcerpt || getTranslatedField(a, "excerpt", lang);
  const category = a._translatedCategory || getTranslatedField(a, "category", lang);
  const image = a.image_url || FALLBACK(640, 360);

  return (
    <Link to={a.href} className="pro-list-row">
      <div className="list-media">
        <img src={imgOrPlaceholder(image, 400, 260)} alt={title} loading="lazy" />
      </div>
      <div className="list-body">
        <div className="list-meta-top">
          {category && <span className="list-category">{category}</span>}
          <div className="meta-dot" />
          <PostMeta date={a.published_at || a.created_at} locale={lang} />
        </div>
        <h4 className="list-title line-clamp-2">{title}</h4>
        {excerpt && <p className="list-excerpt line-clamp-2">{excerpt}</p>}
      </div>
    </Link>
  );
}

/* --------- Main Component --------- */
const NewsGridThree = ({ title = "news", articles = [], height }) => {
  const { lang: providerLang, t } = useTranslation();
  const [view, setView] = useState("grid");
  const [fetched, setFetched] = useState([]);
  const [loading, setLoading] = useState(false);
  const [itemsLimit, setItemsLimit] = useState(6);

  const safeLang = useMemo(() => {
    const candidate = providerLang || DEFAULT_LANG;
    return SAFE_LOCALES.includes(candidate) ? candidate : DEFAULT_LANG;
  }, [providerLang]);

  // Responsive logic for item count
  useEffect(() => {
    const updateLimit = () => {
      const w = window.innerWidth;
      if (w < 576) setItemsLimit(2);
      else if (w < 992) setItemsLimit(4);
      else if (w < 1200) setItemsLimit(6);
      else setItemsLimit(9);
    };
    updateLimit();
    window.addEventListener("resize", updateLimit);
    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  // API Data Fetching
  useEffect(() => {
    let cancelled = false;
    const fetchNews = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/articles/`, { 
          params: { limit: 12, lang: safeLang },
          headers: { "Accept-Language": safeLang }
        });
        if (!cancelled) {
          const items = Array.isArray(res.data) ? res.data : res.data.results || [];
          setFetched(items.map(a => normalizeArticleForLang(a, safeLang)));
        }
      } catch (err) {
        console.error("[NewsGridThree] fetch error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchNews();
    const interval = setInterval(fetchNews, NEWS_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [safeLang]);

  const list = useMemo(() => {
    const source = articles.length ? articles : fetched;
    return source.slice(0, itemsLimit).map(a => 
      a._translatedTitle ? a : normalizeArticleForLang(a, safeLang)
    );
  }, [articles, fetched, itemsLimit, safeLang]);

  const sectionTitle = t(title) || title;

  return (
    <section className="news-section-wrapper mb-5" aria-label={sectionTitle}>
      <SectionHeader 
        title={sectionTitle} 
        right={
          <div className="pro-view-switcher shadow-sm">
            <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}>
              <i className="bi bi-grid-fill" />
            </button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
              <i className="bi bi-view-list" />
            </button>
          </div>
        }
      />

      <div className="mt-4">
        {loading && list.length === 0 ? (
          <div className="row g-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="col-md-4"><div className="skeleton-loader" style={{ height: 300 }} /></div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-5 border rounded-4 bg-light">
            <p className="text-muted mb-0">{t("no_articles") || "No articles found."}</p>
          </div>
        ) : view === "grid" ? (
          <div className="row g-3 g-lg-4">
            {list.map(a => <GridCard key={a.id || a.slug} a={a} lang={safeLang} />)}
          </div>
        ) : (
          <div className="vstack gap-3">
            {list.map(a => <ListRow key={a.id || a.slug} a={a} lang={safeLang} />)}
          </div>
        )}
      </div>

      <style>{`
        .news-section-wrapper { font-family: 'Inter', sans-serif; }

        /* Modern View Switcher */
        .pro-view-switcher { 
          background: #f1f3f5; 
          border-radius: 12px; 
          padding: 4px; 
          display: flex; 
          gap: 4px; 
          border: 1px solid rgba(0,0,0,0.05);
        }
        .pro-view-switcher button { 
          border: none; 
          background: transparent; 
          padding: 6px 14px; 
          border-radius: 9px; 
          color: #adb5bd; 
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .pro-view-switcher button.active { 
          background: #fff; 
          color: var(--bs-primary); 
          box-shadow: 0 4px 12px rgba(0,0,0,0.08); 
        }

        /* GRID CARD STYLES */
        .pro-grid-card { 
          position: relative; 
          display: block; 
          height: 320px; 
          border-radius: 20px; 
          overflow: hidden; 
          text-decoration: none !important; 
          background: #1a1a1a;
          border: 1px solid rgba(0,0,0,0.05);
        }
        .card-media { height: 100%; width: 100%; overflow: hidden; position: relative; }
        .card-media img { 
          width: 100%; 
          height: 100%; 
          object-fit: cover; 
          transition: transform 0.7s cubic-bezier(0.19, 1, 0.22, 1); 
        }
        .pro-grid-card:hover .card-media img { transform: scale(1.12); opacity: 0.8; }
        
        .grid-category-badge { 
          position: absolute; 
          top: 16px; 
          left: 16px; 
          background: var(--bs-primary); 
          color: #fff; 
          font-size: 0.65rem; 
          font-weight: 800; 
          padding: 5px 12px; 
          border-radius: 8px; 
          text-transform: uppercase; 
          letter-spacing: 0.5px;
          z-index: 2;
        }
        [dir="rtl"] .grid-category-badge { left: auto; right: 16px; }

        .card-overlay { 
          position: absolute; 
          inset: 0; 
          background: linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 50%, transparent 100%); 
          display: flex; 
          align-items: flex-end; 
          padding: 24px; 
          z-index: 1;
        }
        .grid-title { 
          color: #fff; 
          font-size: 1.25rem; 
          font-weight: 700; 
          line-height: 1.3; 
          margin: 0; 
          transition: transform 0.3s ease;
        }
        .pro-grid-card:hover .grid-title { transform: translateY(-5px); }

        /* LIST ROW STYLES */
        .pro-list-row { 
          display: flex; 
          gap: 24px; 
          padding: 16px; 
          background: #fff; 
          border: 1px solid #f1f3f5; 
          border-radius: 20px; 
          text-decoration: none !important; 
          color: inherit; 
          transition: all 0.3s ease; 
        }
        .pro-list-row:hover { 
          border-color: var(--bs-primary); 
          transform: translateX(8px); 
          box-shadow: 0 12px 30px rgba(0,0,0,0.04); 
        }
        [dir="rtl"] .pro-list-row:hover { transform: translateX(-8px); }
        
        .list-media { width: 240px; height: 150px; flex-shrink: 0; border-radius: 14px; overflow: hidden; }
        .list-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
        .pro-list-row:hover .list-media img { transform: scale(1.08); }
        
        .list-body { flex: 1; display: flex; flex-direction: column; justify-content: center; }
        .list-meta-top { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
        .list-category { color: var(--bs-primary); font-weight: 800; font-size: 0.75rem; text-transform: uppercase; }
        .meta-dot { width: 4px; height: 4px; border-radius: 50%; background: #dee2e6; }
        
        .list-title { font-size: 1.35rem; font-weight: 700; color: #212529; line-height: 1.3; margin-bottom: 8px; }
        .list-excerpt { font-size: 0.95rem; color: #6c757d; margin: 0; line-height: 1.5; }

        /* Utils */
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .skeleton-loader { background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; border-radius: 20px; }
        @keyframes loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

        @media (max-width: 768px) {
          .pro-list-row { flex-direction: column; gap: 16px; }
          .list-media { width: 100%; height: 220px; }
          .grid-title { font-size: 1.1rem; }
          .pro-grid-card { height: 280px; }
        }
      `}</style>
    </section>
  );
};

export default NewsGridThree;